import { Box, type SxProps, type Theme } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { SystemTimePicker } from "../UIToolkit/Inputs/SystemTimePicker";

type Props = {
	selectedStart: DateTime;
	selectedEnd: DateTime;
	/** A time of day the user picked for the start; the caller places it on the shift's day. */
	onStartPicked: (time: DateTime) => void;
	/** A time of day the user picked for the end; the caller places it after the start. */
	onEndPicked: (time: DateTime) => void;
	startLabel: string;
	endLabel: string;
	startDataTestId?: string;
	endDataTestId?: string;
	disabled: boolean;
	sx: SxProps<Theme> | null;
};

/**
 * `TimeRangeSelection`'s From/To as time pickers, for a finger (APP-886). The typed fields opened the phone's keyboard
 * on a tap; on a touch screen `SystemTimePicker` opens a clock instead, and its field cannot be typed into.
 *
 * Only the hour and minute of a pick are read: which calendar day they land on is the shift's rule (the department's
 * day start, an end before the start rolling over midnight), which the caller already owns for the typed fields.
 * A pick arrives on the clock's OK (`openOnTap`), so its Cancel changes nothing; a cleared value is not a time, so it
 * changes nothing either.
 */
export const EditTimeTouchFields: FC<Props> = ({
	selectedStart,
	selectedEnd,
	onStartPicked,
	onEndPicked,
	startLabel,
	endLabel,
	startDataTestId,
	endDataTestId,
	disabled,
	sx,
}) => (
	<Box sx={[{ display: "flex", flexDirection: "column", gap: 2 }, ...(sx === null ? [] : Array.isArray(sx) ? sx : [sx])]}>
		<SystemTimePicker
			value={selectedStart}
			onChange={(time) => {
				if (time?.isValid) onStartPicked(time);
			}}
			label={startLabel}
			disabled={disabled}
			openOnTap
			dataTestId={startDataTestId}
		/>
		<SystemTimePicker
			value={selectedEnd}
			onChange={(time) => {
				if (time?.isValid) onEndPicked(time);
			}}
			label={endLabel}
			disabled={disabled}
			openOnTap
			dataTestId={endDataTestId}
		/>
	</Box>
);
